// @ts-nocheck
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Eye, EyeOff } from 'lucide-react';
import { useSportsByGender } from '@/hooks/useSportsByGender';
import { usePositionsBySport } from '@/hooks/usePositionsBySport';
import { states } from '@/lib/constants';

const athleteSchema = z.object({
  name: z.string().min(2, 'Name is required'),
  email: z.string().email('Valid email required'),
  username: z.string().min(3, 'Username must be at least 3 characters'),
  phone: z.string().regex(/^\d{3}-\d{3}-\d{4}$/, 'Format: xxx-xxx-xxxx'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  city: z.string().min(2, 'City is required'),
  state: z.string().min(2, 'State is required'),
  gender: z.enum(['Men', 'Women']),
  sport_id: z.string().min(1, 'Sport is required'),
  position_id: z.string().optional(),
  education_level: z.enum(['high_school', 'college']),
  year: z.string().min(1, 'Year is required'),
  agreeToTerms: z.boolean().refine(val => val === true, 'You must agree to terms'),
});

type AthleteFormData = z.infer<typeof athleteSchema>;

interface MobileAthleteRegistrationProps {
  onSuccess: () => void;
  onBack: () => void;
}

const MobileAthleteRegistration: React.FC<MobileAthleteRegistrationProps> = ({ 
  onSuccess,
  onBack 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<AthleteFormData>({
    resolver: zodResolver(athleteSchema),
    defaultValues: {
      agreeToTerms: false
    }
  });

  const selectedGender = watch('gender');
  const selectedSportId = watch('sport_id');
  const selectedEducationLevel = watch('education_level');
  
  const { sports, isLoading: sportsLoading } = useSportsByGender(selectedGender);
  const { positions, isLoading: positionsLoading } = usePositionsBySport(selectedSportId);

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    
    if (selectedEducationLevel === 'high_school') {
      const options = [];
      for (let year = currentYear; year <= 2035; year++) {
        options.push({ value: year.toString(), label: year.toString() });
      }
      return options;
    } else if (selectedEducationLevel === 'college') {
      return [
        { value: 'freshman', label: 'Freshman' },
        { value: 'sophomore', label: 'Sophomore' },
        { value: 'junior', label: 'Junior' },
        { value: 'senior', label: 'Senior' },
        { value: 'graduate', label: 'Graduate' },
      ];
    }
    return [];
  };

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const onSubmit = async (data: AthleteFormData) => {
    setIsLoading(true);
    
    try {
      // Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            name: data.name,
            username: data.username,
            role: 'athlete'
          },
          emailRedirectTo: `${window.location.origin}/`
        }
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Failed to create account');

      // Wait for profile trigger
      await new Promise(resolve => setTimeout(resolve, 500));

      // Update profile
      const profileData = {
        id: authData.user.id,
        user_id: authData.user.id,
        name: data.name,
        username: data.username,
        email: data.email,
        phone: data.phone,
        city: data.city,
        state: data.state,
        gender: data.gender,
        sport_id: data.sport_id,
        primary_position_id: data.position_id || null,
        education_level: data.education_level,
        college_year: selectedEducationLevel === 'college' ? data.year : null,
        grad_year: selectedEducationLevel === 'high_school' ? parseInt(data.year) : null,
        agree_terms: data.agreeToTerms,
        role: 'athlete',
        primary_role_slug: 'athlete',
        membership_level: 'free',
        active_membership_slug: 'athlete_free',
      };

      const { error: profileError } = await supabase
        .from('profiles')
        .upsert(profileData, { 
          onConflict: 'user_id',
          ignoreDuplicates: false 
        });

      if (profileError) {
        console.error('Profile error:', profileError);
        throw new Error('Failed to complete registration');
      }

      // RLE has no membership plans; nothing to assign.

      toast.success('Account created successfully!');
      onSuccess();
    } catch (error: any) {
      console.error('Registration error:', error);
      toast.error(error.message || 'Failed to create account');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 bg-background border-b border-border z-10">
        <div className="flex items-center gap-3 px-4 h-14">
          <button onClick={onBack} className="p-1">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-semibold">Athlete Registration</h1>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 pb-8">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Basic Info */}
          <div className="space-y-2">
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              placeholder="Enter full name"
              {...register('name')}
              disabled={isLoading}
            />
            {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter email"
              {...register('email')}
              disabled={isLoading}
            />
            {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              placeholder="Choose username"
              {...register('username')}
              disabled={isLoading}
            />
            {errors.username && <p className="text-sm text-destructive">{errors.username.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              placeholder="xxx-xxx-xxxx"
              {...register('phone')}
              onChange={(e) => {
                const formatted = formatPhoneNumber(e.target.value);
                setValue('phone', formatted);
              }}
              disabled={isLoading}
            />
            {errors.phone && <p className="text-sm text-destructive">{errors.phone.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter password"
                {...register('password')}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && <p className="text-sm text-destructive">{errors.password.message}</p>}
          </div>

          {/* Location */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                placeholder="City"
                {...register('city')}
                disabled={isLoading}
              />
              {errors.city && <p className="text-sm text-destructive">{errors.city.message}</p>}
            </div>
            <div className="space-y-2">
              <Label>State</Label>
              <Select onValueChange={(value) => setValue('state', value)} disabled={isLoading}>
                <SelectTrigger>
                  <SelectValue placeholder="State" />
                </SelectTrigger>
                <SelectContent>
                  {states.map((state) => (
                    <SelectItem key={state.value} value={state.value}>
                      {state.value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.state && <p className="text-sm text-destructive">{errors.state.message}</p>}
            </div>
          </div>

          {/* Gender */}
          <div className="space-y-2">
            <Label>NCAA Gender Classification</Label>
            <Select onValueChange={(value) => setValue('gender', value as 'Men' | 'Women')} disabled={isLoading}>
              <SelectTrigger>
                <SelectValue placeholder="Select gender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Men">Men</SelectItem>
                <SelectItem value="Women">Women</SelectItem>
              </SelectContent>
            </Select>
            {errors.gender && <p className="text-sm text-destructive">{errors.gender.message}</p>}
          </div>

          {/* Sport & Position */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Sport</Label>
              <Select 
                onValueChange={(value) => setValue('sport_id', value)} 
                disabled={isLoading || sportsLoading || !selectedGender}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sport" />
                </SelectTrigger>
                <SelectContent>
                  {sports?.map((sport) => (
                    <SelectItem key={sport.id} value={sport.id}>
                      {sport.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.sport_id && <p className="text-sm text-destructive">{errors.sport_id.message}</p>}
            </div>
            <div className="space-y-2">
              <Label>Position</Label>
              <Select 
                onValueChange={(value) => setValue('position_id', value)} 
                disabled={isLoading || positionsLoading || !selectedSportId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Position" />
                </SelectTrigger>
                <SelectContent>
                  {positions?.map((pos) => (
                    <SelectItem key={pos.id} value={pos.id}>
                      {pos.position_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Education */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Education Level</Label>
              <Select 
                onValueChange={(value) => setValue('education_level', value as 'high_school' | 'college')} 
                disabled={isLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high_school">High School</SelectItem>
                  <SelectItem value="college">College</SelectItem>
                </SelectContent>
              </Select>
              {errors.education_level && <p className="text-sm text-destructive">{errors.education_level.message}</p>}
            </div>
            <div className="space-y-2">
              <Label>{selectedEducationLevel === 'college' ? 'Year' : 'Grad Year'}</Label>
              <Select 
                onValueChange={(value) => setValue('year', value)} 
                disabled={isLoading || !selectedEducationLevel}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {getYearOptions().map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.year && <p className="text-sm text-destructive">{errors.year.message}</p>}
            </div>
          </div>

          {/* Terms */}
          <div className="flex items-start space-x-3 pt-2">
            <Checkbox
              id="agreeToTerms"
              onCheckedChange={(checked) => setValue('agreeToTerms', checked as boolean)}
              disabled={isLoading}
            />
            <Label htmlFor="agreeToTerms" className="text-sm leading-tight">
              I agree to the Terms of Service and Privacy Policy
            </Label>
          </div>
          {errors.agreeToTerms && (
            <p className="text-sm text-destructive">{errors.agreeToTerms.message}</p>
          )}

          <Button 
            type="submit" 
            className="w-full h-12 mt-4" 
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating Account...
              </>
            ) : (
              'Create Athlete Account'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default MobileAthleteRegistration;
